// When the Book page has to keep asking. The poll is one GET /api/books/:id
// every 2 s while a job is doing something (ADR 0007: single Author, single
// replica, progress commits seconds apart), and off when nothing is running,
// so an idle tab is silent.
//
// Pure predicates, no React: BookLayout and the dashboard both read them and
// tests/client/poll.test.ts drives them without a DOM.
import type { BookDetail, BookState, BookSummary } from "@contract";

export const POLL_INTERVAL_MS = 2000;

/**
 * Book states in which the service still has work in hand, for a list row that
 * carries no job rows. `parsed` is in the set because the Chapter Proposal job
 * starts as soon as the Book is Parsed (DESIGN "States"), so a list that
 * stopped there would never see the proposal finish or fail.
 *
 * The dashboard card's spinner reads the same set, so a state can never be one
 * the list polls on and the card shows as idle.
 */
export const WORKING_BOOK_STATES: ReadonlySet<BookState> = new Set<BookState>([
  "uploaded",
  "parsed",
  "confirmed",
  "styleSheetReady",
  "calibrating",
  "refining",
  "rendering",
]);

/**
 * Whether anything is running on this Book: a queued or running job, a
 * Chapter mid-Refinement or mid-Render, or a Book render that has not settled.
 * A job parked for a manual retry is not work in flight, so a Book waiting for
 * the Author's retry click stops the poll instead of asking forever.
 */
export function bookIsWorking(book: BookDetail | undefined): boolean {
  if (!book) return false;
  if (book.activeJobs.some((job) => job.status === "queued" || job.status === "running")) {
    return true;
  }
  if (book.chapters.some((c) => c.state === "refining" || c.state === "rendering")) return true;
  const status = book.render?.status;
  return status === "rendering" || status === "stopping" || status === "assembling";
}

/** What TanStack Query's refetchInterval wants: the interval, or false to stop. */
export const bookPollInterval = (book: BookDetail | undefined): number | false =>
  bookIsWorking(book) ? POLL_INTERVAL_MS : false;

/** The dashboard list has no job rows, so it polls on the Book states that mean a job is running. */
export const bookListIsWorking = (books: readonly BookSummary[] | undefined): boolean =>
  (books ?? []).some((book) => WORKING_BOOK_STATES.has(book.state));

export const bookListPollInterval = (
  response: { books: readonly BookSummary[] } | undefined,
): number | false => (bookListIsWorking(response?.books) ? POLL_INTERVAL_MS : false);
